import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Receipt, 
  Save,
  AlertCircle,
  CheckCircle,
  Search,
  Info
} from 'lucide-react';
import { receiptsAPI, clientsAPI } from '../services/api';

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
}

interface ManualReceiptFormData {
  client: string;
  invoiceNumber: string;
  amount: number;
  paymentMode: string;
  referenceNumber: string;
  bankName: string;
  chequeNumber: string;
  transactionDate: string;
  notes: string;
  tdsPercentage: number;
  tdsDeducted: number;
}

const ManualReceipt: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ManualReceiptFormData>({
    client: '',
    invoiceNumber: '',
    amount: 0,
    paymentMode: 'Bank Transfer',
    referenceNumber: '',
    bankName: '',
    chequeNumber: '',
    transactionDate: new Date().toISOString().split('T')[0],
    notes: '',
    tdsPercentage: 0,
    tdsDeducted: 0
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({ limit: 1000 });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setMessage({ type: 'error', text: 'Failed to load clients' });
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (clientId: string) => {
    const client = clients.find(c => c._id === clientId);
    setSelectedClient(client || null);
    setFormData(prev => ({ ...prev, client: clientId }));
  };

  const handleInputChange = (field: keyof ManualReceiptFormData, value: string | number) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate TDS deducted when percentage or amount changes
      // TDS should be calculated on gross amount (amount field for manual receipts)
      // Note: For manual receipts, amount is the gross amount before tax
      if (field === 'tdsPercentage' || field === 'amount') {
        const amount = field === 'amount' ? value as number : newData.amount;
        const tdsPercentage = field === 'tdsPercentage' ? value as number : newData.tdsPercentage;
        const tdsDeducted = (amount * tdsPercentage) / 100;
        newData.tdsDeducted = Math.round(tdsDeducted * 100) / 100;
      }
      
      return newData;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      // Validation
      if (!formData.client) {
        setMessage({ type: 'error', text: 'Please select a client' });
        setSaving(false);
        return;
      }

      if (!formData.amount || formData.amount <= 0) {
        setMessage({ type: 'error', text: 'Please enter a valid amount' });
        setSaving(false);
        return;
      }

      const paymentDetails: any = {
        transactionDate: formData.transactionDate || new Date()
      };

      if (formData.referenceNumber) {
        paymentDetails.referenceNumber = formData.referenceNumber;
      }

      if (formData.bankName) {
        paymentDetails.bankName = formData.bankName;
      }

      if (formData.chequeNumber) {
        paymentDetails.chequeNumber = formData.chequeNumber;
      }

      // Round off amount
      const roundedAmount = Math.round(formData.amount);
      
      await receiptsAPI.create({
        client: formData.client,
        invoiceNumber: formData.invoiceNumber,
        amount: roundedAmount,
        paymentMode: formData.paymentMode,
        paymentDetails,
        notes: formData.notes,
        isManual: true,
        tdsPercentage: formData.tdsPercentage,
        tdsDeducted: formData.tdsDeducted
      });

      setMessage({ type: 'success', text: 'Receipt created successfully!' });
      
      // Redirect to all receipts after 1.5 seconds
      setTimeout(() => {
        navigate('/receipts');
      }, 1500);
    } catch (error) {
      console.error('Error creating receipt:', error);
      setMessage({ type: 'error', text: 'Failed to create receipt' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Receipt className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Manual Receipt</h1>
          <p className="text-gray-600">Manually create a receipt for payment received</p>
        </div>
      </div>

      {/* Info Message */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3"
      >
        <Info className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Manual Receipt Information</p>
          <p>Use this page to create receipts for payments received outside of the invoice system. This is useful for advance payments or other transactions.</p>
        </div>
      </motion.div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div>
            <label className="form-label">Select Client *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={formData.client}
                onChange={(e) => handleClientSelect(e.target.value)}
                className="form-input pl-10"
                required
              >
                <option value="">Choose a client...</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.clientName} - {client.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Selected Client Info */}
          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-gray-50 rounded-lg"
            >
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Name:</strong> {selectedClient.clientName}</p>
                <p><strong>Email:</strong> {selectedClient.email}</p>
                <p><strong>Contact:</strong> {selectedClient.contactNumber}</p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Receipt Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Receipt Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Invoice Number (if applicable)</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                className="form-input"
                placeholder="Enter invoice number or N/A"
              />
            </div>

            <div>
              <label className="form-label">Gross Amount *</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                className="form-input"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="form-label">TDS Percentage (%)</label>
              <input
                type="number"
                value={formData.tdsPercentage}
                onChange={(e) => handleInputChange('tdsPercentage', parseFloat(e.target.value) || 0)}
                className="form-input"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 10"
              />
            </div>

            <div>
              <label className="form-label">TDS Deducted (Auto-calculated)</label>
              <input
                type="number"
                value={formData.tdsDeducted}
                readOnly
                className="form-input bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Automatically calculated from gross amount and TDS %</p>
            </div>

            <div>
              <label className="form-label">Payment Mode *</label>
              <select
                value={formData.paymentMode}
                onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                className="form-input"
                required
              >
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="form-label">Transaction Date</label>
              <input
                type="date"
                value={formData.transactionDate}
                onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                className="form-input"
              />
            </div>

            {formData.paymentMode === 'Bank Transfer' && (
              <>
                <div>
                  <label className="form-label">Reference Number</label>
                  <input
                    type="text"
                    value={formData.referenceNumber}
                    onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Bank Name</label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    className="form-input"
                  />
                </div>
              </>
            )}

            {formData.paymentMode === 'Cheque' && (
              <>
                <div>
                  <label className="form-label">Cheque Number</label>
                  <input
                    type="text"
                    value={formData.chequeNumber}
                    onChange={(e) => handleInputChange('chequeNumber', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Bank Name</label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    className="form-input"
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-6">
            <label className="form-label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="form-input"
              placeholder="Additional notes or comments..."
            />
          </div>
        </motion.div>

        {/* Summary */}
        {formData.amount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card bg-primary-50 border-primary-200"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Gross Amount:</span>
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(formData.amount)}
                </span>
              </div>
              {formData.tdsDeducted > 0 && (
                <>
                  <div className="flex justify-between items-center border-t border-primary-200 pt-3">
                    <span className="text-sm text-gray-600">TDS Deducted ({formData.tdsPercentage}%):</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(formData.tdsDeducted)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t border-primary-300 pt-3">
                    <span className="text-lg font-bold text-gray-900">Net Amount Received:</span>
                    <span className="text-2xl font-bold text-primary-600">
                      {formatCurrency(formData.amount - formData.tdsDeducted)}
                    </span>
                  </div>
                </>
              )}
              {formData.tdsDeducted === 0 && (
                <div className="flex justify-between items-center border-t border-primary-300 pt-3">
                  <span className="text-lg font-bold text-gray-900">Amount Received:</span>
                  <span className="text-2xl font-bold text-primary-600">
                    {formatCurrency(formData.amount)}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Form Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-end space-x-4"
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Creating...' : 'Create Receipt'}</span>
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default ManualReceipt;

